import { useHandler } from "@redotech/react-util/hook";
import { FulfillmentOrderLineItem } from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import {
  RedoTable,
  RedoTableLayout,
} from "@redotech/redo-web/arbiter-components/tables/redo-table";
import { StandardRedoTableHeaderCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-header-cells";
import { Flex } from "@redotech/redo-web/flex";
import { MoneyText } from "@redotech/redo-web/fx/money-text";
import { toMoney } from "@redotech/redo-web/fx/to-money";
import { Text } from "@redotech/redo-web/text";
import {
  GroupInput,
  Input,
  InputSchema,
  ListInput,
  groupInput,
  input,
  listInput,
} from "@redotech/ui/form";
import classNames from "classnames";
import { memo } from "react";
import * as styles from "./modal-items-tables.module.css";

export const splitGroupForm = groupInput({
  data: listInput(
    () => input<number>(),
    () => 0,
    (_, index) => index,
  ),
});

export type SplitGroupForm = InputSchema.Input<typeof splitGroupForm>;
export type SplitGroupFormValue = InputSchema.Value<typeof splitGroupForm>;
export const splitGroupFormDefault: SplitGroupFormValue = { data: [] };

interface LineItemWithInput extends FulfillmentOrderLineItem {
  input: Input<number>;
}

const getItemTableLayout = (): RedoTableLayout<LineItemWithInput> => {
  return {
    columns: [
      {
        key: "sku",
        widthMode: { type: "fixed", width: 120 },
        renderHeaderCell: () => (
          <Flex
            align="center"
            borderColor="primary"
            borderStyle="solid"
            borderTopWidth="1px"
            height="full"
            pl="3xl"
            w="full"
          >
            <Text fontSize="xxs" fontWeight="medium" textColor="secondary">
              Sku
            </Text>
          </Flex>
        ),
        renderNormalCell: ({ row }) => {
          return (
            <Flex p="xl" pl="3xl">
              <Text fontSize="xs" fontWeight="medium" textColor="primary">
                {row.sku}
              </Text>
            </Flex>
          );
        },
      },
      {
        key: "title",
        widthMode: { type: "fill" },
        renderHeaderCell: () => (
          <StandardRedoTableHeaderCell headerText="Item" />
        ),
        renderNormalCell: ({ row }) => {
          return (
            <>
              <Flex p="sm">
                <img
                  className={styles.productImage}
                  src={row?.imageUrl ?? undefined}
                />
              </Flex>
              <Flex flexDirection="column" gap="none" justify="center">
                <Text
                  className={styles.ellipsesOverflow}
                  fontSize="sm"
                  fontWeight="medium"
                >
                  {row.productTitle || row.title}
                </Text>
                {row?.variantTitle !== "Default Title" && (
                  <Text
                    className={styles.ellipsesOverflow}
                    fontSize="xxs"
                    textColor="secondary"
                  >
                    {row.variantTitle.replace(" / ", ", ")}
                  </Text>
                )}
              </Flex>
            </>
          );
        },
        alignment: "left",
      },
      {
        key: "price",
        widthMode: { type: "hug" },
        renderHeaderCell: () => (
          <StandardRedoTableHeaderCell headerText="Price" />
        ),
        renderNormalCell: ({ row }) => {
          return (
            <Flex p="xl" pl="3xl">
              <Text fontSize="xs" fontWeight="medium" textColor="primary">
                <MoneyText
                  struck
                  value={toMoney({
                    amount: row.unitPrice.amount,
                    currency: row.unitPrice.currency,
                  })}
                />
              </Text>
            </Flex>
          );
        },
        alignment: "left",
      },
      {
        key: "quantity",
        widthMode: { type: "hug" },
        renderHeaderCell: () => (
          <StandardRedoTableHeaderCell headerText="Quantity" />
        ),
        renderNormalCell: ({ row }) => {
          return (
            <LineItemIncrementDecrement input={row.input} lineItem={row} />
          );
        },
        alignment: "left",
      },
    ],
  };
};

export const ModalItemsTable = memo(function ModalItemsTable({
  items,
  form,
}: {
  form: GroupInput<{ data: ListInput<number, Input<number>> }>;
  items: FulfillmentOrderLineItem[];
}) {
  return (
    <RedoTable
      additionalClasses={classNames(styles.standaloneTable)}
      key={items.length}
      layout={getItemTableLayout()}
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      rowsOfData={items.map((item, index) => ({
        ...item,
        input: form.inputs.data.inputs[index],
      }))}
    />
  );
});

export function LineItemIncrementDecrement({
  input,
  lineItem,
}: {
  input: Input<number>;
  lineItem: FulfillmentOrderLineItem;
}) {
  const update = useHandler((value: number) => {
    input.setValue(value);
  });
  return (
    <Flex dir="row" gap="md" justify="space-between" px="lg" w="full">
      <RedoIncrementDecrement
        max={lineItem.unfulfilledQuantity}
        min={0}
        setValue={update}
        size="small"
        step={1}
        suffix={`of ${lineItem.unfulfilledQuantity}`}
        value={input.value}
      />
    </Flex>
  );
}
